import { z } from 'zod';

// Generic validation schemas that can be reused across components

// Date validation schema
export const createDateSchema = (t: (_key: string) => string, isRequired = true) => {
  return isRequired
    ? z.string().min(1, t('validation.dateRequired'))
    : z.string().optional();
};

// Comment/description validation schema
export const createCommentSchema = (t: (_key: string, _params?: Record<string, unknown>) => string, maxLength = 1000, isRequired = false) => {
  const schema = z.string().max(maxLength, t('validation.commentTooLong', { max: maxLength }));
  return isRequired ? schema.min(1, t('validation.commentRequired')) : schema.optional();
};

// Number validation schema
export const createNumberSchema = (
  t: (_key: string, _params?: Record<string, unknown>) => string,
  options: {
    min?: number;
    max?: number;
    required?: boolean;
    integer?: boolean;
  } = {}
) => {
  const { min, max, required = false, integer = false } = options;

  let schema = integer ? z.number().int() : z.number();

  if (min !== undefined) {
    schema = schema.min(min, t('validation.numberTooSmall', { min }));
  }
  if (max !== undefined) {
    schema = schema.max(max, t('validation.numberTooLarge', { max }));
  }

  return required ? schema : schema.optional();
};

// Email validation schema
export const createEmailSchema = (t: (_key: string) => string) => {
  return z.string()
    .min(1, t('validation.emailRequired'))
    .email(t('validation.emailInvalid'));
};

// File validation
export const validateFileConstraints = (
  file: File,
  constraints: {
    maxSizeBytes?: number;
    allowedTypes?: string[];
    maxNameLength?: number;
  },
  t: (_key: string, _params?: Record<string, unknown>) => string
): { isValid: boolean; error?: string } => {
  const { maxSizeBytes, allowedTypes, maxNameLength } = constraints;

  if (maxSizeBytes && file.size > maxSizeBytes) {
    const maxSizeMB = Math.round(maxSizeBytes / (1024 * 1024));
    return {
      isValid: false,
      error: t('validation.fileTooLarge', { maxSize: `${maxSizeMB}MB` })
    };
  }

  if (allowedTypes && !allowedTypes.some(type => file.type.startsWith(type))) {
    return {
      isValid: false,
      error: t('validation.fileTypeNotAllowed')
    };
  }

  if (maxNameLength && file.name.length > maxNameLength) {
    return {
      isValid: false,
      error: t('validation.fileNameTooLong', { maxLength: maxNameLength })
    };
  }

  return { isValid: true };
};

// Unified truck quantity fields validation schema
export const createTruckQuantitySchema = (t: (_key: string, _params?: Record<string, unknown>) => string) => ({
  packingListQuantity: z.coerce.number({
    invalid_type_error: t('validation.numberInvalidType')
  }).nonnegative({ message: t('validation.mustBeNonNegative') }).optional(),

  palletQuantity: z.coerce.number({
    invalid_type_error: t('validation.numberInvalidType')
  }).nonnegative({ message: t('validation.mustBeNonNegative') }).optional(),

});

// Outbound truck specific validation schema (extends base truck schema)
export const createOutboundTruckSchema = (t: (_key: string, _params?: Record<string, unknown>) => string) => z.object({
  date: z.string({ required_error: t('validation.dateRequired') })
    .min(1, t('validation.dateRequired')),

  initialComment: z.string().optional(),

  isFinal: z.boolean().optional(),

  // Required fields for outbound trucks
  boxQuantity: z.coerce.number({
    required_error: t('validation.boxQuantityRequired'),
    invalid_type_error: t('validation.numberInvalidType')
  }).positive({ message: t('validation.boxQuantityPositive') }),

  unitsPerBox: z.coerce.number({
    required_error: t('validation.unitsPerBoxRequired'),
    invalid_type_error: t('validation.numberInvalidType')
  }).positive({ message: t('validation.unitsPerBoxPositive') }),

  // Optional quantity fields
  ...createTruckQuantitySchema(t),

  // Upload confirmation fields
  deliveryNoteUploaded: z.boolean().optional(),
  packingListUploaded: z.boolean().optional(),
});

// Common field validation schemas

// Name validation schema with configurable constraints
export const createNameSchema = (
  t: (_key: string, _params?: Record<string, unknown>) => string,
  options: {
    minLength?: number;
    maxLength?: number;
    required?: boolean;
    fieldName?: string;
  } = {}
) => {
  const { minLength = 1, maxLength = 255, required = true, fieldName = 'name' } = options;

  let schema = z.string()
    .max(maxLength, t('validation.nameTooLong', { max: maxLength, field: fieldName }));

  if (required) {
    schema = schema.min(minLength, t('validation.nameRequired', { field: fieldName }));
  }

  // Add common name character validation (excludes control characters and some special symbols)
  // eslint-disable-next-line no-control-regex
  schema = schema.regex(/^[^\x00-\x1F\x7F<>{}|\\]+$/, {
    message: t('validation.nameInvalidCharacters', { field: fieldName })
  });

  return required ? schema : schema.optional();
};

// Person name validation (first/last name)
export const createPersonNameSchema = (
  t: (_key: string, _options?: Record<string, unknown>) => string,
  fieldName: 'firstName' | 'lastName',
  required = true
) => {
  let schema = z.string()
    .max(100, t('validation.personNameTooLong', { max: 100, field: fieldName }));

  if (required) {
    schema = schema.min(1, t(`validation.${fieldName}Required`));
  }

  return required ? schema : schema.optional();
};

// Phone number validation schema
export const createPhoneSchema = (t: (_key: string, _params?: Record<string, unknown>) => string, required = false) => {
  const schema = z.string()
    .max(50, t('validation.phoneTooLong'));  // Only max length, no format validation - allows any international format

  return required ? schema : schema.optional();
};

// Company type validation
export const createCompanyTypeSchema = (t: (_key: string, _params?: Record<string, unknown>) => string, companyTypes: readonly string[]) => {
  return z.enum(companyTypes as [string, ...string[]], {
    required_error: t('validation.companyTypeRequired'),
  });
};

// Role validation schema
export const createRoleSchema = (t: (_key: string, _params?: Record<string, unknown>) => string, roles: readonly string[]) => {
  return z.enum(roles as [string, ...string[]], {
    required_error: t('validation.roleRequired'),
  });
};